import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  fieldProblems,
  financeError,
  useAddDocument,
  useAudit,
  useInvoice,
  useSetStatus,
} from '../../hooks/useFinance';
import InvoiceFiles, { StatusBadge } from './InvoiceFiles';
import InvoiceForm from './InvoiceForm';
import {
  DOCUMENT_KINDS,
  FILE_ACCEPT,
  INPUT,
  MAX_LABEL,
  NEXT_STATUSES,
  PRIMARY,
  SECONDARY,
  STATUS_LABELS,
  formatAmount,
  fileProblem,
  formatDateTime,
  periodLabel,
} from './financeText';

function StatusChange({ invoice }) {
  const setStatus = useSetStatus();
  const [target, setTarget] = useState(null);
  const [note, setNote] = useState('');
  const options = NEXT_STATUSES[invoice.status] ?? [];
  if (!options.length) {
    return (
      <p className="text-xs text-gray-500">
        {invoice.status === 'paid'
          ? 'Paid is final.'
          : 'A rejected invoice comes back for review when it’s corrected.'}
      </p>
    );
  }
  const noteRequired = target === 'rejected';
  const run = () =>
    setStatus.mutate(
      { id: invoice.id, status: target, note: note.trim() },
      {
        onSuccess: () => {
          toast.success(`Marked as ${STATUS_LABELS[target].toLowerCase()}.`);
          setTarget(null);
          setNote('');
        },
        onError: (error) =>
          toast.error(financeError(error, "We couldn't change the status.")),
      }
    );
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {options.map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setTarget(status)}
            className={target === status ? PRIMARY : SECONDARY}
          >
            {status === 'approved' && 'Approve'}
            {status === 'paid' && 'Mark as paid'}
            {status === 'rejected' && 'Reject…'}
          </button>
        ))}
      </div>
      {target && (
        <div className="space-y-2 rounded-lg border border-gray-200 p-3">
          <textarea
            rows={2}
            aria-label="Note"
            placeholder={
              noteRequired
                ? 'Why it’s rejected (the consultant sees this)'
                : 'Note (optional)'
            }
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={`${INPUT} w-full`}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={run}
              disabled={setStatus.isLoading || (noteRequired && !note.trim())}
              className={PRIMARY}
            >
              {setStatus.isLoading
                ? 'Saving…'
                : `Confirm: ${STATUS_LABELS[target]}`}
            </button>
            <button
              type="button"
              onClick={() => setTarget(null)}
              className={SECONDARY}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const DOCUMENT_OPTIONS = DOCUMENT_KINDS.map(({ kind, label }) => ({
  kind,
  label: kind === 'other' ? 'Other' : label,
}));

// Attach a payslip, proof of payment or another document to the invoice.
function AddDocument({ invoice }) {
  const add = useAddDocument();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('payslip');
  const [label, setLabel] = useState('');
  const [file, setFile] = useState(null);
  const [problems, setProblems] = useState({});
  const reset = () => {
    setOpen(false);
    setKind('payslip');
    setLabel('');
    setFile(null);
    setProblems({});
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={SECONDARY}>
        Add document
      </button>
    );
  }
  const labelProblem =
    (kind === 'other' && !label.trim() && 'Add a label for this document.') ||
    (label.length > MAX_LABEL &&
      `Keep the label to ${MAX_LABEL} characters.`) ||
    null;
  const fileError = fileProblem(file) || problems.file;
  const submit = () => {
    const form = new FormData();
    form.append('kind', kind);
    if (label.trim()) form.append('label', label.trim());
    form.append('file', file, file.name);
    add.mutate(
      { id: invoice.id, form },
      {
        onSuccess: () => {
          toast.success(
            kind === 'payslip'
              ? 'Payslip added. The consultant has been emailed.'
              : 'Document added.'
          );
          reset();
        },
        onError: (error) => {
          setProblems(fieldProblems(error));
          toast.error(financeError(error, "We couldn't add the document."));
        },
      }
    );
  };
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          Type
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className={INPUT}
          >
            {DOCUMENT_OPTIONS.map((o) => (
              <option key={o.kind} value={o.kind}>
                {o.label}
              </option>
            ))}
          </select>
          {problems.kind && (
            <span className="text-[11px] text-red-700">{problems.kind}</span>
          )}
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          {kind === 'other' ? 'Label' : 'Label (optional)'}
          <input
            type="text"
            maxLength={MAX_LABEL}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={kind === 'other' ? 'e.g. Contract amendment' : ''}
            className={INPUT}
          />
          {(problems.label || (label && labelProblem)) && (
            <span className="text-[11px] text-red-700">
              {problems.label || labelProblem}
            </span>
          )}
        </label>
      </div>
      <div className="flex flex-col gap-1">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          File
          <input
            type="file"
            accept={FILE_ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-xs text-gray-700"
          />
        </label>
        <span
          className={`text-[11px] ${
            fileError ? 'text-red-700' : 'text-gray-500'
          }`}
        >
          {fileError || 'PDF, JPG or PNG, up to 10 MB'}
        </span>
      </div>
      {kind !== 'other' && (
        <p className="text-[11px] text-gray-500">
          A new {kind === 'payslip' ? 'payslip' : 'proof of payment'} replaces
          the current one; the earlier version is kept.
          {kind === 'payslip' &&
            ' The consultant is emailed that their payslip is available.'}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={
            !file ||
            Boolean(fileProblem(file)) ||
            Boolean(labelProblem) ||
            add.isLoading
          }
          className={PRIMARY}
        >
          {add.isLoading ? 'Uploading…' : 'Upload'}
        </button>
        <button type="button" onClick={reset} className={SECONDARY}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function SubmissionAudit({ id }) {
  const { data, isLoading } = useAudit({ submissionId: id });
  if (isLoading)
    return <p className="text-xs text-gray-500">Loading history…</p>;
  const entries = data?.entries ?? [];
  if (!entries.length)
    return <p className="text-xs text-gray-500">No history yet.</p>;
  return (
    <ul className="m-0 list-none space-y-1 p-0">
      {entries.map((e) => (
        <li key={e.id} className="text-xs text-gray-600">
          <span className="text-gray-400">{formatDateTime(e.at)}</span>{' '}
          {e.actor_email || 'system'} · {e.action}
        </li>
      ))}
    </ul>
  );
}

// One invoice for a finance holder: details, files, status and history.
export default function HolderInvoiceDetail({ id, onClose }) {
  const { data: invoice, isLoading, error } = useInvoice(id, 'all');
  const [editing, setEditing] = useState(false);
  if (isLoading)
    return (
      <div
        className="h-40 rounded bg-gray-100 animate-pulse"
        aria-label="Loading"
      />
    );
  if (error)
    return (
      <p className="text-sm text-gray-600">
        {financeError(error, "We couldn't load this invoice.")}
      </p>
    );
  if (editing) {
    return (
      <InvoiceForm
        scope="all"
        invoice={invoice}
        onDone={() => setEditing(false)}
        onCancel={() => setEditing(false)}
      />
    );
  }
  return (
    <div className="space-y-5 text-left">
      <div className="space-y-1">
        <p className="text-sm text-gray-900">
          {invoice.administrator_name}{' '}
          <span className="text-xs text-gray-500">
            {invoice.administrator_email}
          </span>
        </p>
        <div className="flex items-center gap-2">
          <StatusBadge status={invoice.status} />
          <span className="text-sm text-gray-700">
            {periodLabel(invoice.period)} ·{' '}
            {formatAmount(invoice.amount, invoice.currency)}
          </span>
        </div>
        {invoice.status_note && (
          <p className="text-xs text-gray-600">Note: {invoice.status_note}</p>
        )}
        {invoice.reviewed_at && (
          <p className="text-xs text-gray-500">
            Reviewed {formatDateTime(invoice.reviewed_at)}
            {invoice.reviewed_by && ` by ${invoice.reviewed_by}`}
          </p>
        )}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <dt className="text-gray-500">Invoice number</dt>
        <dd className="text-gray-900">{invoice.invoice_number}</dd>
        <dt className="text-gray-500">eTIMS invoice number</dt>
        <dd className="text-gray-900">{invoice.etims_invoice_number}</dd>
        <dt className="text-gray-500">KRA PIN</dt>
        <dd className="font-mono text-gray-900">{invoice.kra_pin}</dd>
        <dt className="text-gray-500">Submitted</dt>
        <dd className="text-gray-900">
          {formatDateTime(invoice.created_at)}
          {invoice.created_by && ` by ${invoice.created_by}`}
        </dd>
      </dl>
      <InvoiceFiles
        scope="all"
        invoice={invoice}
        documentsEmptyText="No documents attached yet."
      />
      <AddDocument invoice={invoice} />
      <section className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Status
        </p>
        <StatusChange invoice={invoice} />
      </section>
      <section className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          History
        </p>
        <SubmissionAudit id={invoice.id} />
      </section>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className={SECONDARY}>
          Close
        </button>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={PRIMARY}
        >
          Edit or replace files
        </button>
      </div>
    </div>
  );
}
